import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export default class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message || 'Unknown error' };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[AppErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen px-6" style={{ backgroundColor: '#0f0f12', color: '#e5e5e5' }}>
          <div className="max-w-sm w-full text-center space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto">
              <i className="ri-error-warning-line text-red-400 text-2xl"></i>
            </div>
            <div>
              <h1 className="text-base font-bold mb-1">Something went wrong</h1>
              <p className="text-xs opacity-60 leading-relaxed">
                The app crashed while loading. Try refreshing or going back to the dashboard.
              </p>
            </div>
            {this.state.errorMessage && (
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-[10px] font-mono text-red-300 break-words">{this.state.errorMessage}</p>
              </div>
            )}
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1"></i>
                Reload
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 rounded-lg text-xs font-medium bg-white/10 text-white hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line mr-1"></i>
                Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}